import { Controller } from "@hotwired/stimulus"

const NEAR_BOTTOM_THRESHOLD = 120
// One person writing inside this window writes a single block: past it the
// conversation stopped, and saying who is talking is worth the line again.
const GROUP_WINDOW_SECONDS = 10 * 60

export default class extends Controller {
  // "message" has to be declared even though the callback already fired
  // without it: the messageTargets list — the thread in order — only exists
  // for a declared target.
  static targets = ["messages", "message", "scroller", "editor", "clientId"]
  static values = { currentUserId: String, cardId: String, readingUrl: String }

  connect() {
    // Before going down to the end: blocks and day separators decide the
    // height of the thread, and the end is where it is once they are in.
    this.#group()
    this.#scrollToBottom()
    requestAnimationFrame(() => this.#scrollToBottom())
    // Opening the thread is what marks it read — the GET that rendered it
    // writes nothing, because Turbo prefetches it on hover.
    this.#markRead()
    this.visibilityHandler = () => {
      if (!document.hidden && this.#nearBottom) this.#markRead()
    }
    document.addEventListener("visibilitychange", this.visibilityHandler)
  }

  disconnect() {
    document.removeEventListener("visibilitychange", this.visibilityHandler)
    clearTimeout(this.readTimer)
    cancelAnimationFrame(this.groupingFrame)
  }

  messageTargetConnected(message) {
    if (message.dataset.creatorId === this.currentUserIdValue) {
      message.setAttribute("data-mine", "")
    }
    this.#scheduleGrouping()
  }

  beforeStreamRender(event) {
    const stream = event.detail.newStream
    const render = event.detail.render

    if (stream.hasAttribute("maintain-scroll")) {
      event.detail.render = async (streamElement) => {
        const height = this.scrollerTarget.scrollHeight
        await render(streamElement)
        this.scrollerTarget.scrollTop += this.scrollerTarget.scrollHeight - height
      }
    } else if (this.#appendsToThread(stream)) {
      const wasNearBottom = this.#nearBottom
      event.detail.render = async (streamElement) => {
        await render(streamElement)
        if (wasNearBottom) {
          this.#scrollToBottom()
          this.#markRead()
        }
      }
    } else if (this.#summarisesThisChat(stream)) {
      // The sender broadcasts the summary to every participant without
      // knowing who has the thread on screen. Whoever does owns the answer:
      // if the newest message is in view, it was read the moment it landed —
      // and only the "new" half is silenced, because what was last said is
      // still worth reading in the list.
      event.detail.render = async (streamElement) => {
        await render(streamElement)
        if (this.#nearBottom && !document.hidden) this.#markRead()
      }
    }
  }

  // Scrolling down to the newest message is reading it: the durable marker
  // follows the eye, not the navigation.
  scrolled() {
    if (this.#nearBottom && this.#unreadMarker?.classList.contains("hidden") === false) this.#markRead()
  }

  ensureContent(event) {
    if (this.editorTarget.value.trim().length === 0) event.preventDefault()
  }

  submitByKeyboard(event) {
    if (event.key !== "Enter" || event.shiftKey || event.isComposing || this.#usingTouchDevice) return

    event.preventDefault()
    this.clientIdTarget.form.requestSubmit()
  }

  submitEnd(event) {
    if (!event.detail.success) return

    this.editorTarget.value = ""
    this.resize()
    this.clientIdTarget.value = crypto.randomUUID()
    this.editorTarget.focus()
    this.#scrollToBottom()
  }

  resize() {
    this.editorTarget.style.height = "auto"
    this.editorTarget.style.height = `${Math.min(this.editorTarget.scrollHeight, 128)}px`
  }

  // Reading is debounced because one Turbo response can append several
  // messages. The endpoint re-checks CardPolicy; this request grants nothing.
  // The selected dot disappears immediately and the durable marker follows.
  #markRead() {
    this.#unreadMarker?.classList.add("hidden")

    clearTimeout(this.readTimer)
    this.readTimer = setTimeout(() => {
      fetch(this.readingUrlValue, {
        method: "PATCH",
        headers: {
          "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")?.content,
          "Accept": "text/html"
        },
        credentials: "same-origin"
      })
    }, 250)
  }

  // Who opens a block and who announces the day is only knowable by looking
  // at the message above, and the one above changes: a new message arrives
  // through a stream knowing nothing of what is on screen, and an older page
  // is inserted on top of everything. So the whole thread is gone over —
  // fifty comparisons of two data attributes — instead of patching the piece
  // that moved. Inside a requestAnimationFrame, which runs before the paint:
  // no message is ever seen unfolded and then folded back.
  #scheduleGrouping() {
    this.groupingFrame ||= requestAnimationFrame(() => {
      this.groupingFrame = null
      this.#group()
    })
  }

  #group() {
    const wasNearBottom = this.#nearBottom
    const fromBottom = this.scrollerTarget.scrollHeight - this.scrollerTarget.scrollTop
    let previous = null

    for (const message of this.messageTargets) {
      const sameDay = previous?.dataset.chatDay === message.dataset.chatDay
      const continues = sameDay &&
        previous.dataset.creatorId === message.dataset.creatorId &&
        Number(message.dataset.chatAt) - Number(previous.dataset.chatAt) < GROUP_WINDOW_SECONDS

      message.toggleAttribute("data-grouped", continues)
      message.toggleAttribute("data-day-start", !sameDay)
      previous = message
    }

    // Folding headers and opening separators moves the height of the whole
    // thread: the reading is anchored to the bottom, where the conversation
    // is happening.
    if (wasNearBottom) this.#scrollToBottom()
    else this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight - fromBottom
  }

  #appendsToThread(stream) {
    return stream.getAttribute("action") === "append" &&
      stream.getAttribute("target") === this.messagesTarget.id
  }

  #summarisesThisChat(stream) {
    return stream.getAttribute("action") === "replace" &&
      stream.getAttribute("target") === `summary_card_${this.cardIdValue}`
  }

  // The "N new" half of this conversation's row in the list. The preview
  // beside it is nobody's business here: it is the same for everybody.
  get #unreadMarker() {
    return document.querySelector(`#summary_card_${this.cardIdValue} [data-chat-unread]`)
  }

  get #nearBottom() {
    return this.scrollerTarget.scrollTop + this.scrollerTarget.clientHeight >=
      this.scrollerTarget.scrollHeight - NEAR_BOTTOM_THRESHOLD
  }

  #scrollToBottom() {
    this.scrollerTarget.scrollTop = this.scrollerTarget.scrollHeight
  }

  get #usingTouchDevice() {
    return "ontouchstart" in window || navigator.maxTouchPoints > 0
  }
}
